import { makeClassName } from '@siteless/util';
import { useEffect, useRef } from 'react';
import { underwaterLight } from './config';

type Particle = {
  x: number;
  y: number;
  speedY: number;
  wobbleAmp: number;
  wobbleFreq: number;
  phase: number;
  radius: number;
  opacity: number;
  age: number;
  maxAge: number;
};

let PARTICLE_MIN_AGE = 800;
let PARTICLE_MAX_AGE = 2400;

let createParticle = (width: number, height: number): Particle => ({
  x: Math.random() * width,
  y: Math.random() * height,
  speedY: 0.01 + Math.random() * 0.03,
  wobbleAmp: 0.05 + Math.random() * 0.15,
  wobbleFreq: 0.001 + Math.random() * 0.003,
  phase: Math.random() * Math.PI * 2,
  radius: 0.4 + Math.random() * 1.2,
  opacity: 1,
  age: 0,
  maxAge: PARTICLE_MIN_AGE + Math.random() * (PARTICLE_MAX_AGE - PARTICLE_MIN_AGE),
});

let UnderwaterParticles = ({
  className,
  style,
  count = 84,
}: {
  className?: string;
  style?: React.CSSProperties;
  count?: number;
}) => {
  let canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    let canvas = canvasRef.current;
    if (!canvas) return;
    let ctx = canvas.getContext('2d');
    if (!ctx) return;

    let width = canvas.clientWidth;
    let height = canvas.clientHeight;
    let dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.scale(dpr, dpr);

    let particles: Particle[] = Array.from({ length: count }, () => {
      let p = createParticle(width, height);
      p.age = Math.random() * p.maxAge;
      return p;
    });

    let t = 0;
    let frameId: number;

    let draw = () => {
      frameId = requestAnimationFrame(draw);
      t += 0.05;
      ctx!.clearRect(0, 0, width, height);
      for (let p of particles) {
        p.y -= p.speedY;
        p.age++;
        let prevWobble = Math.sin((t - 0.05) * p.wobbleFreq + p.phase);
        let currWobble = Math.sin(t * p.wobbleFreq + p.phase);
        p.x += (currWobble - prevWobble) * p.wobbleAmp * 1200;

        if (p.y < -5 || p.age >= p.maxAge) {
          Object.assign(p, createParticle(width, height));
        }

        let fadeIn = Math.min(p.age / 60, 1);
        let fadeOut = Math.min((p.maxAge - p.age) / 60, 1);
        ctx!.globalAlpha = p.opacity * fadeIn * fadeOut;
        ctx!.beginPath();
        ctx!.fillStyle = underwaterLight;
        ctx!.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx!.fill();
      }
    };

    frameId = requestAnimationFrame(draw);

    let onResize = () => {
      width = canvas!.clientWidth;
      height = canvas!.clientHeight;
      canvas!.width = width * dpr;
      canvas!.height = height * dpr;
      ctx!.scale(dpr, dpr);
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('resize', onResize);
    };
  }, [count]);

  return (
    <canvas
      ref={canvasRef}
      className={makeClassName('absolute inset-0 pointer-events-none', className)}
      style={{ width: '100%', height: '100%', ...style }}
    />
  );
};

export default UnderwaterParticles;
